import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Container } from "reactstrap";

import ReactTable from "components/ReactTable/ReactTable";
import useFetch from "Hooks/useFetch";
import styles from "Pages/Users/ManageRoles/ManageRoles.module.css";
import ManageRolesModal from "./ManageRolesModal/ManageRolesModal";
import usePageTitle from "Hooks/usePageTitle";

interface pageData {
    roles: IRole[];
    integrations: IIntegration[];
}

type ActionType = "new" | "edit" | "delete" | "";

export default function ManageRoles() {
    const [pageData, setPageData] = useState<pageData>({
        roles: [],
        integrations: [],
    });
    const [activeModal, setActiveModal] = useState<ActionType>("");
    const [isLoading, setIsLoading] = useState(false);
    const [selectedRow, setSelectedRow] = useState<any>(undefined);

    usePageTitle("Manage Roles");
    
    const { get } = useFetch();

    const ignore = useRef(false);

    const getRoles = useCallback(async () => {
        if (ignore.current) return;

        setIsLoading(true);

        const response = await get(`api/users/roles`);

        if (response.ok && !ignore.current) {
            const data = await response.json();
            console.log(
                "Manage Roles request for roles and integrations: ",
                data
            );
            setPageData(data);
        }
        setIsLoading(false);
    }, [get]);

    // Fetches roles on page load
    useEffect(() => {
        ignore.current = false;

        getRoles();

        return () => {
            ignore.current = true;
        };
    }, [getRoles]);

    const onSetActiveModal = useCallback((modal: ActionType) => {
        setActiveModal(modal);
    }, []);

    function onSetSelectedRow(row: any) {
        setSelectedRow(row);
    }

    const editRow = useCallback((row: any) => {
        setActiveModal("edit");
        setSelectedRow(row);
    }, []);

    const columns = useMemo(() => {
        const cellContent = (value: string) => {
            return <span className={styles.textBox}>{value}</span>;
        };
        return [
            {
                Header: "Role",
                accessor: "name",
                maxWidth: "50",
                Cell: ({ value }: { value: string }) => cellContent(value),
            },
            {
                Header: "Permissions",
                accessor: "permissions",
                Cell: ({ value }: { value: string }) => cellContent(value),
            },
        ];
    }, []);

    const tableProps = {
        columns,
        data: pageData.roles,
        showPagination: true,
        onSetSelectedRow,
        rowDoubleClickCallback: editRow,
        isLoading,
    };

    const userModalProps = {
        activeModal,
        onSetActiveModal,
        onSetSelectedRow,
        selectedRow,
        roles: pageData.roles,
        integrations: pageData.integrations,
        getRoles,
    };

    return (
        <Container fluid>
            <div className={styles.headerAndButtonsBox}>
                <h1>Manage Roles</h1>
                <span className={styles.buttonsBox}>
                    <Button
                        color="primary"
                        onClick={() => setActiveModal("new")}
                    >
                        New
                    </Button>
                    <Button
                        color="success"
                        onClick={() => setActiveModal("edit")}
                        disabled={!selectedRow}
                    >
                        Edit
                    </Button>
                    <Button
                        color="danger"
                        onClick={() => setActiveModal("delete")}
                        disabled={!selectedRow}
                    >
                        Delete
                    </Button>
                </span>
            </div>

            <ReactTable {...tableProps} />
            <ManageRolesModal {...userModalProps} />
        </Container>
    );
}
